import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatMoney, formatQty } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { Article, Partner } from '../types';

interface Verkaufspreis {
  id: string;
  article_id: string;
  sku: string;
  article_name: string;
  customer_id: string | null;
  customer_name: string | null;
  min_qty: number;
  price_cents: number;
  valid_from: string;
  valid_to: string | null;
  note: string | null;
}

interface Abrechenbar {
  id: string;
  number: string;
  customer_name: string | null;
  shipped_at: string | null;
  net_cents: number;
  ohne_preis: number;
}

interface Entwurfszeile {
  description: string;
  qty: number;
  unit: string;
  unitPriceCents: number;
  discountPercent: number;
  amountCents: number;
  taxRatePercent: number;
}

interface Rechnungsentwurf {
  orderId: string;
  orderNumber: string;
  customerName: string;
  lieferdatum: string;
  lines: Entwurfszeile[];
  netCents: number;
  taxCents: number;
  grossCents: number;
  taxNote: string | null;
  warnungen: string[];
}

export function SalesPage({ modus }: { modus: 'rechnung' | 'preise' }) {
  const [geladen, setGeladen] = useState(false);
  const [preise, setPreise] = useState<Verkaufspreis[] | null>(null);
  const [abrechenbar, setAbrechenbar] = useState<Abrechenbar[]>([]);
  const [kunden, setKunden] = useState<Partner[]>([]);
  const [entwurf, setEntwurf] = useState<Rechnungsentwurf | null>(null);

  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<Article[]>([]);
  const [formular, setFormular] = useState<{
    articleId: string;
    sku: string;
    customerId: string;
    minQty: string;
    preis: string;
    validFrom: string;
    validTo: string;
  } | null>(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      if (modus === 'rechnung') {
        setAbrechenbar(await api<Abrechenbar[]>('/api/sales/invoiceable'));
      } else {
        const [preisListe, partnerListe] = await Promise.all([
          api<Verkaufspreis[]>('/api/sales/prices'),
          api<Partner[]>('/api/partners', { query: { role: 'customer' } }),
        ]);
        setPreise(preisListe);
        setKunden(partnerListe);
      }
      setGeladen(true);
    } catch (err) {
      setError(errorText(err));
    }
  }, [modus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (suche.trim().length < 2) {
      setTreffer([]);
      return;
    }
    let aktuell = true;
    api<Article[]>('/api/articles', { query: { search: suche, stockableOnly: 'true' } })
      .then((liste) => aktuell && setTreffer(liste.slice(0, 6)))
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, [suche]);

  async function preisSpeichern() {
    if (!formular) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/sales/prices', {
        method: 'PUT',
        body: {
          articleId: formular.articleId,
          customerId: formular.customerId || null,
          minQty: Number(formular.minQty.replace(',', '.')) || 0,
          priceCents: Math.round(Number(formular.preis.replace(',', '.')) * 100 || 0),
          validFrom: formular.validFrom || null,
          validTo: formular.validTo || null,
        },
      });
      setMessage('Preis gespeichert.');
      setFormular(null);
      setSuche('');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function preisLoeschen(id: string) {
    setBusy(true);
    try {
      await api(`/api/sales/prices/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function vorschau(orderId: string) {
    setError('');
    setEntwurf(null);
    try {
      setEntwurf(await api<Rechnungsentwurf>(`/api/sales/orders/${orderId}/invoice/preview`));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function rechnungErzeugen(orderId: string) {
    setBusy(true);
    setError('');
    try {
      const rechnung = await api<{ number: string; gross_cents: number }>(
        `/api/sales/orders/${orderId}/invoice`,
        { method: 'POST' },
      );
      setMessage(
        `Rechnung ${rechnung.number} über ${formatMoney(rechnung.gross_cents)} erstellt. ` +
          'Sie steht jetzt unter Abrechnung und in den offenen Posten.',
      );
      setEntwurf(null);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!geladen) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {modus === 'rechnung' && (
        <>
      <Card title="Versendet und noch nicht berechnet" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Berechnet wird, was <strong>versendet</strong> wurde — nicht was bestellt war. Die
          Warenrechnung läuft im selben Nummernkreis wie die Lagerabrechnung und landet danach in
          den offenen Posten.
        </div>
        {abrechenbar.length === 0 ? (
          <Empty>Alles berechnet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Auftrag</th>
                <th>Kunde</th>
                <th>Versendet</th>
                <th className="num">Netto</th>
                <th>Hinweis</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {abrechenbar.map((auftrag) => (
                <tr key={auftrag.id}>
                  <td className="mono small">{auftrag.number}</td>
                  <td className="small">{auftrag.customer_name ?? '—'}</td>
                  <td className="small">
                    {auftrag.shipped_at ? formatDate(auftrag.shipped_at) : '—'}
                  </td>
                  <td className="num">{formatMoney(auftrag.net_cents)}</td>
                  <td>
                    {auftrag.ohne_preis > 0 && (
                      <Badge kind="warn">{auftrag.ohne_preis} Position ohne Preis</Badge>
                    )}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => vorschau(auftrag.id)}>
                      prüfen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {entwurf && (
        <Card
          title={`Rechnungsentwurf zu ${entwurf.orderNumber}`}
          actions={
            <>
              <button
                className="primary small"
                onClick={() => rechnungErzeugen(entwurf.orderId)}
                disabled={busy}
              >
                Rechnung erstellen
              </button>
              <button className="small" onClick={() => setEntwurf(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
            <div className="small muted">
              {entwurf.customerName} · geliefert am {formatDate(entwurf.lieferdatum)}
            </div>
            {entwurf.warnungen.map((warnung, index) => (
              <div key={index} style={{ marginTop: 8 }}>
                <Notice kind="info">{warnung}</Notice>
              </div>
            ))}
          </div>
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th className="num">Menge</th>
                <th>Einheit</th>
                <th className="num">Einzelpreis</th>
                <th className="num">Rabatt</th>
                <th className="num">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {entwurf.lines.map((zeile, index) => (
                <tr key={index}>
                  <td>{zeile.description}</td>
                  <td className="num">{formatQty(zeile.qty)}</td>
                  <td className="small">{zeile.unit}</td>
                  <td className="num">{formatMoney(zeile.unitPriceCents)}</td>
                  <td className="num">{zeile.discountPercent > 0 ? `${zeile.discountPercent} %` : '—'}</td>
                  <td className="num">{formatMoney(zeile.amountCents)}</td>
                </tr>
              ))}
              <tr>
                <td colSpan={5} style={{ textAlign: 'right' }}>
                  Netto
                </td>
                <td className="num">{formatMoney(entwurf.netCents)}</td>
              </tr>
              <tr>
                <td colSpan={5} style={{ textAlign: 'right' }}>
                  Umsatzsteuer
                </td>
                <td className="num">{formatMoney(entwurf.taxCents)}</td>
              </tr>
              <tr>
                <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>
                  Zahlbetrag
                </td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {formatMoney(entwurf.grossCents)}
                </td>
              </tr>
            </tbody>
          </table>
          {entwurf.taxNote && <div className="body small muted">{entwurf.taxNote}</div>}
        </Card>
      )}
        </>
      )}

      {modus === 'preise' && (
      <Card
        title="Verkaufspreise"
        actions={
          <button
            className="small"
            onClick={() =>
              setFormular(
                formular
                  ? null
                  : { articleId: '', sku: '', customerId: '', minQty: '0', preis: '', validFrom: '', validTo: '' },
              )
            }
          >
            {formular ? 'Abbrechen' : 'Preis hinterlegen'}
          </button>
        }
        padded={false}
      >
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Drei Stufen, von speziell nach allgemein: Kundenpreis mit Mengenstaffel, Kundenpreis ohne
          Staffel, Listenpreis am Artikel. Der spezifischste gültige Satz gewinnt. Ermittelt wird
          der Preis einmal beim Anlegen des Belegs und dort festgeschrieben — eine spätere Änderung
          verteuert keinen bestehenden Auftrag.
        </div>

        {formular && (
          <div className="body" style={{ borderBottom: '1px solid var(--line)' }}>
            {formular.articleId ? (
              <div className="chosen">
                <strong className="mono">{formular.sku}</strong>
                <span className="spacer" />
                <button
                  className="small ghost"
                  onClick={() => setFormular({ ...formular, articleId: '', sku: '' })}
                >
                  ändern
                </button>
              </div>
            ) : (
              <>
                <TextField
                  label="Artikel suchen"
                  value={suche}
                  onChange={setSuche}
                  placeholder="Artikelnummer oder Name"
                />
                {treffer.length > 0 && (
                  <div className="pick-list" style={{ marginBottom: 14 }}>
                    {treffer.map((artikel) => (
                      <button
                        key={artikel.id}
                        onClick={() =>
                          setFormular({ ...formular, articleId: artikel.id, sku: artikel.sku })
                        }
                      >
                        <strong>{artikel.name}</strong>
                        <div className="small muted">{artikel.sku}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="form-grid">
              <label className="field">
                <span>Gilt für</span>
                <select
                  value={formular.customerId}
                  onChange={(e) => setFormular({ ...formular, customerId: e.target.value })}
                >
                  <option value="">alle Kunden (Preisliste)</option>
                  {kunden.map((kunde) => (
                    <option key={kunde.id} value={kunde.id}>
                      nur {kunde.name}
                    </option>
                  ))}
                </select>
              </label>
              <TextField
                label="Ab Menge"
                value={formular.minQty}
                onChange={(v) => setFormular({ ...formular, minQty: v })}
                hint="0 = ab der ersten Einheit"
              />
              <TextField
                label="Preis je Einheit (€)"
                value={formular.preis}
                onChange={(v) => setFormular({ ...formular, preis: v })}
              />
              <TextField
                label="Gültig ab (optional)"
                type="date"
                value={formular.validFrom}
                onChange={(v) => setFormular({ ...formular, validFrom: v })}
                hint="leer = ab heute; für Aktionen künftiges Datum"
              />
              <TextField
                label="Gültig bis (optional)"
                type="date"
                value={formular.validTo}
                onChange={(v) => setFormular({ ...formular, validTo: v })}
              />
            </div>
            <button
              className="primary small"
              onClick={preisSpeichern}
              disabled={busy || !formular.articleId}
            >
              speichern
            </button>
          </div>
        )}

        {(preise ?? []).length === 0 ? (
          <Empty>Noch keine besonderen Preise — es gilt der Listenpreis aus dem Artikelstamm.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Gilt für</th>
                <th className="num">Ab Menge</th>
                <th className="num">Preis</th>
                <th>Gültig</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(preise ?? []).map((preis) => (
                <tr key={preis.id}>
                  <td>
                    <span className="mono small">{preis.sku}</span> {preis.article_name}
                  </td>
                  <td className="small">
                    {preis.customer_name ? (
                      <Badge kind="info">{preis.customer_name}</Badge>
                    ) : (
                      <span className="muted">alle</span>
                    )}
                  </td>
                  <td className="num">{preis.min_qty > 0 ? formatQty(preis.min_qty) : '—'}</td>
                  <td className="num">{formatMoney(preis.price_cents)}</td>
                  <td className="small muted">
                    ab {formatDate(preis.valid_from)}
                    {preis.valid_to ? ` bis ${formatDate(preis.valid_to)}` : ''}
                  </td>
                  <td className="num">
                    <button
                      className="small ghost"
                      onClick={() => preisLoeschen(preis.id)}
                      disabled={busy}
                    >
                      entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      )}
    </>
  );
}
